import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import styles from './BoardsSearch.less';

import { useStarredBoards } from './useStarredBoards';
import { useBoardsSearchQuery } from './BoardsSearchQuery.generated';

import { ModelCache } from 'app/scripts/db/model-cache';

import { CompactBoardTile } from 'app/src/components/CompactBoardTile';
import { getRecentBoards } from 'app/src/components/RecentlyViewedBoardsMenu/recentBoardsState';

import { uniqBy, buildComparator } from '@trello/arrays';
import { forTemplate } from '@trello/i18n';
import { Key } from '@trello/keybindings';
import { navigate } from 'app/scripts/controller/navigate';
import { HeaderTestIds } from '@trello/test-ids';
import { memberId } from '@trello/session-cookie';
import { Spinner } from '@trello/nachos/spinner';
import { buildFuzzyMatcher } from '@trello/strings';
import { dynamicDebounce } from '@trello/time';
import { makeLocalUrl } from '@trello/urls';

const format = forTemplate('boards_sidebar');

interface Props {
  forwardedRef?: React.Ref<HTMLInputElement>;
  // For testing
  searchText?: string;
}

interface MinimalCachedBoard {
  id: string;
  name: string;
  url: string;
}

function getCachedBoards(): MinimalCachedBoard[] {
  return ModelCache.all('Board').map((b) => ({
    id: b.attributes.id,
    name: b.attributes.name,
    url: b.attributes.url,
  }));
}

function getMyBoards(): string[] {
  const me = memberId ? ModelCache.get('Member', memberId) : null;
  return me?.attributes?.idBoards || [];
}

const BoardsSearchComponent = (props: Props) => {
  const MAX_BOARD_RESULTS = 10;
  const { boardsMenu, addStar, removeStar } = useStarredBoards();

  const [searchText, setSearchText] = useState(props.searchText || '');
  const [visibleBoards, setVisibleBoards] = useState<MinimalCachedBoard[]>([]);
  const [selectedBoard, setSelectedBoard] = useState<MinimalCachedBoard | null>(
    null,
  );
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const { data: searchData, loading } = useBoardsSearchQuery({
    variables: {
      query: debouncedSearchQuery,
      limit: MAX_BOARD_RESULTS,
    },
    skip: debouncedSearchQuery.length === 0,
  });

  useEffect(() => {
    // filtering
    const isMatch = buildFuzzyMatcher(searchText);
    const boards = uniqBy(
      [
        ...getCachedBoards(),
        ...((searchData?.boardsSearch || []) as MinimalCachedBoard[]),
      ],
      (b) => b.id,
    );
    const filteredBoards = boards.filter(({ name }) => isMatch(name));

    // sorting
    const idBoardsStarred = new Set(boardsMenu.idBoardsStarred);
    const idBoardsRecent = new Set(getRecentBoards().map((b) => b.id));
    const idBoardsMe = new Set(getMyBoards());

    const startsWithSearch = (board: MinimalCachedBoard): boolean => {
      return board.name.toLowerCase().startsWith(searchText.toLowerCase());
    };

    let comparePrefixedBoards:
      | ((a: MinimalCachedBoard, b: MinimalCachedBoard) => number)
      | undefined;

    const sortedBoards = filteredBoards.sort(
      buildComparator(
        // Preferred if it's starred
        (board: MinimalCachedBoard) => idBoardsStarred.has(board.id),
        // Preferred if it's recent
        (board: MinimalCachedBoard) => idBoardsRecent.has(board.id),
        // Preferred if we're a member of the board
        (board: MinimalCachedBoard) => idBoardsMe.has(board.id),
        // Preferred if the name of the board starts our search term, and if
        // both do, by the shorter of the two names
        (a: MinimalCachedBoard, b: MinimalCachedBoard) => {
          if ([a, b].some(startsWithSearch)) {
            if (comparePrefixedBoards === undefined) {
              comparePrefixedBoards = buildComparator(
                // Prefer if it starts with the search term
                (board: MinimalCachedBoard) => startsWithSearch(board),
                // Prefer the shorter name
                (aWithPrefix, bWithPrefix) =>
                  aWithPrefix.name.length - bWithPrefix.name.length,
              );
            }

            return comparePrefixedBoards(a, b);
          } else {
            return 0;
          }
        },
        // Sort alphabetically
        (a: MinimalCachedBoard, b: MinimalCachedBoard) =>
          a.name.localeCompare(b.name),
      ),
    );

    setVisibleBoards(sortedBoards.slice(0, MAX_BOARD_RESULTS));
  }, [searchText, searchData?.boardsSearch, boardsMenu.idBoardsStarred]);

  const debouncedSearch = useMemo(
    () =>
      dynamicDebounce(
        (query: string) => {
          setDebouncedSearchQuery(query.trim());
        },
        (query: string) => {
          return query.length <= 3 ? 1000 : 500;
        },
      ),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearch.clear();
    };
    // This should only run on unmount.
    // eslint-disable-next-line @trello/exhaustive-deps
  }, []);

  const getSelectedIndex = useCallback(() => {
    return selectedBoard
      ? visibleBoards.findIndex((board) => board.id === selectedBoard.id)
      : -1;
  }, [selectedBoard, visibleBoards]);

  const toggleBoardStar = useCallback(
    (idBoard) => {
      if (boardsMenu.idBoardsStarred.includes(idBoard)) {
        removeStar(idBoard);
      } else {
        addStar(idBoard);
      }
    },
    [boardsMenu.idBoardsStarred, addStar, removeStar],
  );

  const onUpArrow = useCallback(() => {
    if (!visibleBoards?.length) {
      return;
    }
    const index = getSelectedIndex();
    const next: number = index > 0 ? index - 1 : 0;
    setSelectedBoard(visibleBoards[next]);
  }, [getSelectedIndex, visibleBoards, setSelectedBoard]);

  const onDownArrow = useCallback(() => {
    if (!visibleBoards?.length) {
      return;
    }
    const index = getSelectedIndex();
    const length: number = visibleBoards.length - 1;
    const next: number = index === length ? length : index + 1;
    setSelectedBoard(visibleBoards[next]);
  }, [getSelectedIndex, visibleBoards, setSelectedBoard]);

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      switch (e.key) {
        case Key.ArrowUp:
          onUpArrow();
          break;
        case Key.ArrowDown:
          onDownArrow();
          break;
        default:
          return;
      }
    },
    [onUpArrow, onDownArrow],
  );

  const onSearchSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!visibleBoards || visibleBoards.length === 0) {
        return;
      }

      const index = Math.max(getSelectedIndex(), 0);
      const boardAtIndex = visibleBoards[index];
      if (boardAtIndex?.url) {
        navigate(makeLocalUrl(boardAtIndex.url), { trigger: true });
      }
    },
    [visibleBoards, getSelectedIndex],
  );

  const isSearchNecessary = useCallback(
    (query: string) => {
      // Don't bother searching on just a single character
      if (query.trim().length <= 1) {
        return false;
      }

      // If we can't fill the view with cached board matches, we need to search
      return visibleBoards.length < MAX_BOARD_RESULTS;
    },
    [visibleBoards.length],
  );

  const onChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    async ({ target: { value: rawValue } }) => {
      const val = rawValue.trim();

      setSearchText(rawValue);
      setSelectedBoard(null);

      if (val.length > 0 && isSearchNecessary(rawValue)) {
        debouncedSearch(rawValue);
      } else {
        // A search is no longer necessary
        debouncedSearch.clear();
      }
    },
    [isSearchNecessary, debouncedSearch, setSearchText, setSelectedBoard],
  );

  const { forwardedRef } = props;
  const selectedId = selectedBoard?.id || null;
  const searchInProgress = isSearchNecessary(searchText) && loading;

  return (
    <>
      <form onSubmit={onSearchSubmit} className={styles.boardsMenuSearchForm}>
        <input
          ref={forwardedRef}
          autoComplete="off"
          className={classNames(styles.input)}
          type="text"
          placeholder={format('find-boards-by-name-ellipsis')}
          name="search-boards"
          value={searchText}
          onChange={onChange}
          onKeyDown={onKeyDown}
          data-test-id={HeaderTestIds.BoardsMenuSearch}
          aria-label={format('find-boards-by-name-ellipsis')}
        />
      </form>
      {Boolean(searchText) &&
        visibleBoards.map((board) => (
          <CompactBoardTile
            key={board.id}
            idBoard={board.id}
            focused={board.id === selectedId}
            isStarred={boardsMenu.idBoardsStarred.includes(board.id)}
            toggleBoardStar={toggleBoardStar}
          />
        ))}
      {searchInProgress && <Spinner centered />}
    </>
  );
};

export const BoardsSearch = React.forwardRef<HTMLInputElement, Props>(
  (props, ref) => <BoardsSearchComponent forwardedRef={ref} {...props} />,
);
